import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  CalendarDays, Users, FileText, CheckCircle, AlertCircle, UserCheck,
  MessageSquare, BarChart2, CreditCard, Cake, PartyPopper, UserPlus,
  DoorOpen, Megaphone, Network, Clock,
} from "lucide-react";
import { getDashboardPeople, getDashboardActivity, getDashboardLeaveTrend } from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, tenantHour } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";
import EmployeeDashboard from "./EmployeeDashboard";

const NAVY   = "#1e3a8a";
const TEAL   = "#0f766e";
const AMBER  = "#f59e0b";
const RED    = "#dc2626";
const PURPLE = "#7c3aed";
const GREEN  = "#16a34a";
const SLATE  = "#475569";

// Department slices, in the order they are handed out. Same family as the rest
// of the app's charts; MODULE_COLORS is deliberately not reused — a department
// is not a module and colouring it like one invites the wrong reading.
const DEPT_COLORS = [NAVY, TEAL, PURPLE, AMBER, "#0369a1", "#be123c", "#4d7c0f", SLATE];

/** Roles that run People operations. Mirrors PEOPLE_DASHBOARD_ROLES in
 *  routers/dashboard.py — anyone else gets a 403 from /api/dashboard/people. */
const PEOPLE_OPERATOR_ROLES = ["admin", "hr", "admin_assistant", "super_admin"];

const card: React.CSSProperties = {
  background: "white", borderRadius: 10, padding: "20px 24px",
  border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

// `children` is optional on these local components, and lists wrap ListRow in a
// keyed <div>, because the React types this project resolves neither inject JSX
// children into the props type nor strip `key` — same shape as SectionTitle in
// EmployeeDashboard. Requiring children here is a compile error at every call.
function PanelTitle({ children, sub }: { children?: React.ReactNode; sub?: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{children}</div>
      {sub && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function EmptyRow({ children }: { children?: React.ReactNode }) {
  return <div style={{ textAlign: "center", padding: "28px 0", color: "#64748b", fontSize: 13 }}>{children}</div>;
}

/** "Today" / "Tomorrow" / "in 12 days" — the only thing anyone reads off these
 *  lists is how soon, so lead with that rather than with a formatted date. */
function whenLabel(daysAway: number) {
  if (daysAway <= 0) return "Today";
  if (daysAway === 1) return "Tomorrow";
  return `in ${daysAway} days`;
}

function Pill({ text, tone }: { text: string; tone: "hot" | "warm" | "cool" }) {
  const c = tone === "hot"  ? { bg: "#fef9c3", fg: "#854d0e", bd: "#fde047" }
          : tone === "warm" ? { bg: "#eff6ff", fg: "#1d4ed8", bd: "#bfdbfe" }
          :                   { bg: "#f1f5f9", fg: "#475569", bd: "#e2e8f0" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
      background: c.bg, color: c.fg, border: `1px solid ${c.bd}`,
      padding: "2px 8px", borderRadius: 20,
    }}>{text}</span>
  );
}

function ListRow({ icon, color, title, sub, right }: {
  icon: React.ReactNode; color: string; title: string; sub?: string; right?: React.ReactNode;
}) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "9px 0", borderBottom: "1px solid #f1f5f9",
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: `${color}15`, color,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ── Activity feed styling ──────────────────────────────────────────────────
// The feed is Core-only (leave / expense / helpdesk). Recruitment and Finance
// activity live on their own module dashboards — see get_activity() in
// routers/dashboard.py.
function activityDot(type: string, status: string) {
  if (type === "leave" || type === "expense") {
    return status === "approved" ? GREEN : status === "rejected" ? RED : AMBER;
  }
  if (type === "helpdesk") {
    return status === "resolved" || status === "closed" ? GREEN : status === "open" ? RED : NAVY;
  }
  return PURPLE;
}

function activityIcon(type: string) {
  if (type === "leave")    return <CalendarDays size={14} />;
  if (type === "expense")  return <CreditCard size={14} />;
  if (type === "helpdesk") return <MessageSquare size={14} />;
  return <FileText size={14} />;
}

function LeaveTrendTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>{payload[0].value} leaves approved</div>
    </div>
  );
}

// ── People (Core) dashboard ────────────────────────────────────────────────
// This page is Core's own dashboard, and every widget on it must be Core data.
// It used to carry Recruitment, Finance and Payroll tiles as well, which each of
// those modules now owns in richer form on /recruitment, /finance and
// /payroll-home — so a tenant that had bought none of them still saw the shape
// of all three on its home screen. Anything whose target route resolves to a
// module under moduleForPath() does not belong here.
// Spec: .claude/specs/people-dashboard.md
function PeopleOperatorDashboard() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const navigate = useNavigate();

  const { data: people } = useQuery({
    queryKey: ["dashboard-people"], queryFn: getDashboardPeople, refetchInterval: 60_000,
  });
  const { data: activity = [] } = useQuery({
    queryKey: ["dashboard-activity"], queryFn: getDashboardActivity, refetchInterval: 60_000,
  });
  const { data: trend = [] } = useQuery({
    queryKey: ["dashboard-leave-trend"], queryFn: getDashboardLeaveTrend, staleTime: 5 * 60_000,
  });

  const firstName = (user?.name || "").split(" ")[0];
  const hour = tenantHour(tz);
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dayStr = formatDate(new Date(), "long", tz);

  const att = people?.attendance ?? {};
  const headcount = people?.headcount ?? 0;
  const pendingLeaves = people?.pending_leaves ?? 0;
  const openHelpdesk = people?.open_helpdesk ?? 0;
  const pendingExpenses = people?.pending_expenses ?? 0;
  const pendingLetters = people?.pending_letters ?? 0;
  const exits = people?.exits_in_progress ?? 0;
  const celebrations: any[] = people?.celebrations ?? [];
  const joiners: any[] = people?.new_joiners ?? [];
  const probation: any[] = people?.probation_ending ?? [];
  const departments: any[] = people?.headcount_by_department ?? [];
  const announcements: any[] = people?.announcements ?? [];
  const pulse = people?.pulse ?? { responses: 0, headcount: 0 };
  const pulsePct = pulse.headcount > 0 ? Math.round((pulse.responses / pulse.headcount) * 100) : 0;

  return (
    <div>
      {/* No width cap: every Core page reached from here — Employees, Leaves,
          Attendance, Timesheet, Assets — runs the full content width, so
          capping the dashboard made the layout jump on the app's most-travelled
          path. Completes #448, which did the six module dashboards. StatCard's
          own maxWidth (shipped there) keeps the tiles from stretching. */}

      {/* ── Greeting ── */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
          {greeting}, {firstName} 👋
        </h1>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
          {dayStr} · Here's what your people need today
        </p>
      </div>

      {/* ── Operations tiles ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 24 }} className="stat-cards-row">
        <StatCard label="Headcount" value={headcount} color={NAVY}
          icon={<Users size={18} />} to="/employees" subtitle="Active employees" />
        <StatCard label="Pending Leave Approvals" value={pendingLeaves}
          color={pendingLeaves > 0 ? AMBER : TEAL} icon={<CalendarDays size={18} />} to="/leaves"
          subtitle={pendingLeaves > 0 ? "Awaiting your decision" : "All caught up"} />
        <StatCard label="On Leave Today" value={people?.on_leave_today ?? 0} color={PURPLE}
          icon={<DoorOpen size={18} />} to="/leaves" subtitle="Approved and out" />
        <StatCard label="Attendance Today" value={`${att.marked ?? 0} / ${headcount}`} color={TEAL}
          icon={<Clock size={18} />} to="/attendance"
          subtitle={`${att.present ?? 0} in · ${att.wfh ?? 0} WFH · ${att.absent ?? 0} absent`} />
        <StatCard label="Open Helpdesk" value={openHelpdesk}
          color={openHelpdesk > 0 ? RED : TEAL} icon={<MessageSquare size={18} />} to="/helpdesk"
          subtitle={openHelpdesk > 0 ? "Needs attention" : "All resolved"} />
        <StatCard label="Pending Reimbursements" value={pendingExpenses}
          color={pendingExpenses > 0 ? AMBER : TEAL} icon={<CreditCard size={18} />} to="/expenses"
          subtitle={pendingExpenses > 0 ? "Awaiting approval" : "All settled"} />
        <StatCard label="Letter Requests" value={pendingLetters}
          color={pendingLetters > 0 ? AMBER : TEAL} icon={<FileText size={18} />} to="/letters"
          subtitle={pendingLetters > 0 ? "Awaiting issue" : "None open"} />
        <StatCard label="Exits In Progress" value={exits}
          color={exits > 0 ? RED : TEAL} icon={<DoorOpen size={18} />} to="/exit"
          subtitle={exits > 0 ? "Clearance outstanding" : "None in progress"} />
      </div>

      {/* ── My Team ── */}
      {people?.team_size > 0 && (
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <UserCheck size={16} color={TEAL} /> My Team · {people.team_size} direct report{people.team_size !== 1 ? "s" : ""}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <button type="button" onClick={() => navigate("/leaves")} style={{
              flex: "1 1 160px", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
              background: people.team_pending_leaves > 0 ? "#fef9c3" : "#f8fafc", borderRadius: 10,
              padding: "12px 18px", border: `1px solid ${people.team_pending_leaves > 0 ? "#fde047" : "#e2e8f0"}`,
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: AMBER }}>{people.team_pending_leaves ?? 0}</div>
              <div style={{ fontSize: 12, color: "#475569", fontWeight: 600, marginTop: 3 }}>Pending Leave Requests</div>
            </button>
            <button type="button" onClick={() => navigate("/expenses")} style={{
              flex: "1 1 160px", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
              background: people.team_pending_expenses > 0 ? "#fef2f2" : "#f8fafc", borderRadius: 10,
              padding: "12px 18px", border: `1px solid ${people.team_pending_expenses > 0 ? "#fca5a5" : "#e2e8f0"}`,
            }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: RED }}>{people.team_pending_expenses ?? 0}</div>
              <div style={{ fontSize: 12, color: "#475569", fontWeight: 600, marginTop: 3 }}>Pending Expense Claims</div>
            </button>
          </div>
        </div>
      )}

      {/* ── Quick actions ── */}
      {/* People routes only. A "Post a Job" or "New Invoice" button here is the
          module leak this page was redesigned to remove — those live on
          /recruitment and /finance. */}
      <div style={{ ...card, marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="Leave Approvals" icon={<CheckCircle size={14} />} to="/leaves" color={AMBER} />
          <QuickAction label="Employees" icon={<Users size={14} />} to="/employees" color={NAVY} />
          <QuickAction label="Attendance" icon={<Clock size={14} />} to="/attendance" color={TEAL} />
          <QuickAction label="Announcements" icon={<Megaphone size={14} />} to="/announcements" color={NAVY} />
          <QuickAction label="Helpdesk" icon={<MessageSquare size={14} />} to="/helpdesk" color={RED} />
          <QuickAction label="Pulse Survey" icon={<BarChart2 size={14} />} to="/pulse" color={TEAL} />
          <QuickAction label="Org Chart" icon={<Network size={14} />} to="/org-chart" color={PURPLE} />
          <QuickAction label="Letters" icon={<FileText size={14} />} to="/letters" color={SLATE} />
        </div>
      </div>

      {/* ── Charts: leave trend + headcount by department ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }} className="grid-2col">
        <div style={card}>
          <PanelTitle sub="Approved leaves · last 6 months">Leave Trend</PanelTitle>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={trend as any[]} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
              <Tooltip content={<LeaveTrendTooltip />} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="leaves" fill={NAVY} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={card}>
          <PanelTitle sub={`${headcount} active employee${headcount === 1 ? "" : "s"}`}>Headcount by Department</PanelTitle>
          {departments.length === 0 ? (
            <EmptyRow>No active employees yet.</EmptyRow>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={departments} dataKey="count" nameKey="department"
                  cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {departments.map((_, i) => (
                    <Cell key={i} fill={DEPT_COLORS[i % DEPT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number, n: string) => [`${v} employee${v === 1 ? "" : "s"}`, n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Coming up + joiners & probation ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }} className="grid-2col">
        <div style={card}>
          <PanelTitle sub="Birthdays and work anniversaries · next 30 days">Coming Up</PanelTitle>
          {celebrations.length === 0 ? (
            <EmptyRow>Nothing to celebrate in the next 30 days.</EmptyRow>
          ) : (
            celebrations.map((c, i) => (
              <div key={`${c.type}-${c.id}-${i}`}>
                <ListRow
                  icon={c.type === "birthday" ? <Cake size={14} /> : <PartyPopper size={14} />}
                  color={c.type === "birthday" ? PURPLE : TEAL}
                  title={c.name || "Employee"}
                  sub={c.type === "birthday" ? "Birthday" : `${c.years} year${c.years === 1 ? "" : "s"} with the company`}
                  right={<Pill text={whenLabel(c.days_away)} tone={c.days_away <= 7 ? "hot" : "warm"} />} />
              </div>
            ))
          )}
        </div>

        <div style={card}>
          <PanelTitle sub="New joiners this month and probation ending soon">Joiners & Probation</PanelTitle>
          {joiners.length === 0 && probation.length === 0 ? (
            <EmptyRow>No joiners this month and no probation ending soon.</EmptyRow>
          ) : (
            <>
              {joiners.map((j: any) => (
                <div key={`joiner-${j.id}`}>
                  <ListRow icon={<UserPlus size={14} />} color={GREEN}
                    title={j.name || "Employee"}
                    sub={[j.designation, j.department].filter(Boolean).join(" · ") || "Joined this month"}
                    right={<Pill text={j.joining_date ? formatDate(j.joining_date) : "—"} tone="cool" />} />
                </div>
              ))}
              {probation.map((p: any) => (
                <div key={`prob-${p.id}`}>
                  <ListRow icon={<AlertCircle size={14} />} color={AMBER}
                    title={p.name || "Employee"}
                    sub={p.designation ? `${p.designation} · probation ends` : "Probation ends"}
                    right={<Pill text={whenLabel(p.days_away)} tone={p.days_away <= 7 ? "hot" : "warm"} />} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── Recent activity + pulse & announcements ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="grid-2col">
        <div style={card}>
          <PanelTitle sub="Leave, reimbursements and helpdesk">Recent Activity</PanelTitle>
          {(activity as any[]).length === 0 ? (
            <EmptyRow>No recent activity.</EmptyRow>
          ) : (
            (activity as any[]).slice(0, 7).map((item: any, i: number) => (
              <div key={i}>
                <ListRow icon={activityIcon(item.type)} color={activityDot(item.type, item.status)}
                  title={item.text} sub={item.sub} />
              </div>
            ))
          )}
        </div>

        <div style={card}>
          <PanelTitle sub={`This month · ${pulse.responses} of ${pulse.headcount || headcount} responded`}>
            Pulse & Announcements
          </PanelTitle>

          <button type="button" onClick={() => navigate("/pulse")} style={{
            width: "100%", textAlign: "left", cursor: "pointer", fontFamily: "inherit",
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
            padding: "12px 14px", marginBottom: 14,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Pulse response rate</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: pulsePct >= 60 ? GREEN : pulsePct > 0 ? AMBER : SLATE }}>{pulsePct}%</span>
            </div>
            <div style={{ background: "#e2e8f0", borderRadius: 4, height: 6, overflow: "hidden" }}>
              <div style={{ width: `${Math.min(pulsePct, 100)}%`, height: "100%", background: pulsePct >= 60 ? GREEN : AMBER, borderRadius: 4, transition: "width 0.4s" }} />
            </div>
          </button>

          {announcements.length === 0 ? (
            <EmptyRow>No published announcements.</EmptyRow>
          ) : (
            announcements.map((a: any) => (
              <div key={a.id}>
                <ListRow icon={<Megaphone size={14} />} color={a.pinned ? AMBER : NAVY}
                  title={a.title}
                  sub={[a.pinned ? "Pinned" : null, a.category, a.created_at ? formatDate(a.created_at) : null]
                    .filter(Boolean).join(" · ")} />
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
}

// ── Route entry ────────────────────────────────────────────────────────────
// `/` is the landing route for five roles. Recruiter and Finance are not People
// operators — /api/dashboard/people 403s for them, and /summary already returned
// them a headcount of zero — so rather than an operator dashboard full of
// blanks they get the personal People surface, which is what their Core nav
// actually contains. Their module work is reached at /recruitment and /finance.
export default function DashboardPage() {
  const { user } = useAuth();
  const isPeopleOperator = PEOPLE_OPERATOR_ROLES.includes(user?.role ?? "");

  return isPeopleOperator ? <PeopleOperatorDashboard /> : <EmployeeDashboard />;
}
